import { observer } from 'mobx-react-lite';
import {
  EmployeeReceiptsTableManagerViewModel,
  EmployeeReceiptsTableViewModel
} from '../../receipts/table';
import { ReceiptStatusWidget } from '../../receipts/widgets/statusWidget';
import { useDate } from '../partials/profilePagesPartial';
import { Box } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useStore } from '../../../hooks/useStore';
import { ContentBox } from '../../../lib/components/compound/pages/contentBox';
import { ContentHead } from '../../../lib/components';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useSnackbar } from 'notistack';
import { ReceiptInfo, ReceiptStatusEnum } from '@pxtra-client-lib-legacy/gen';
import Util from '../../../misc/util';
import { ReceiptsTableManager } from '../../receipts/table/manager/component';

export const EmployeeReceiptsOverview = observer(() => {
  const { companyStore, receiptStore, receiptListStore } = useStore();
  const [info, setInfo] = useState<ReceiptInfo>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { employeeId } = useParams();
  const [date] = useDate();
  const [statusFilter, setStatusFilter] = useState<ReceiptStatusEnum | undefined>(undefined);
  const snackbar = useSnackbar();
  const mainTableRef = useGridApiRef();

  const tableManager = useMemo(() => {
    if (!employeeId || !companyStore.company) return;

    const tableViewModel = new EmployeeReceiptsTableViewModel(
      navigate,
      t,
      snackbar,
      receiptStore,
      mainTableRef
    );
    return new EmployeeReceiptsTableManagerViewModel(
      tableViewModel,
      receiptListStore,
      companyStore,
      employeeId
    );
  }, [employeeId, companyStore.company]);

  useEffect(() => {
    const fn = async () => {
      if (!companyStore.company) return;
      if (employeeId && date) {
        tableManager?.setDateFilter(date);
        const dateAfter = Util.getFirstDayOfMonth(date);
        const dateBefore = Util.getLastDayOfMonth(date);
        const info = await receiptStore.getInfo(employeeId, undefined, dateAfter, dateBefore);
        setInfo(info.at(0));
      }
    };
    fn();
  }, [date, companyStore.company]);

  useEffect(() => {
    if (employeeId && date) {
      tableManager?.setStatusFilter(statusFilter);
    }
  }, [statusFilter]);

  const handleStatusWidgetClick = (status: ReceiptStatusEnum) => {
    status === statusFilter ? setStatusFilter(undefined) : setStatusFilter(status);
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridTemplateRows: '142px 1fr',
        gridGap: '1.5rem',
        paddingBottom: '32px'
      }}
    >
      <ReceiptStatusWidget
        submitted={{
          count: info?.submitted_count,
          onClick: () => handleStatusWidgetClick('SUBMITTED')
        }}
        approved={{
          count: info?.approved_count,
          onClick: () => handleStatusWidgetClick('APPROVED')
        }}
        billed={{
          count: info?.billed_count,
          onClick: () => handleStatusWidgetClick('BILLED')
        }}
        rejected={{
          count: info?.rejected_count,
          onClick: () => handleStatusWidgetClick('REJECTED')
        }}
        selectedItem={statusFilter}
      />

      <ContentBox
        headComponent={<ContentHead headline={t('common:RECEIPTS')} showActions={false} />}
        paddingBottom="0"
      >
        {tableManager && <ReceiptsTableManager viewModel={tableManager} />}
      </ContentBox>
    </Box>
  );
});
